import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
  Box,
  LinearProgress,
  Alert,
} from "@mui/material";
import { Sparkles } from "lucide-react";
import Hls from "hls.js";
import { useScrollytellTranslation } from "../Admin/scrollytellContext";
import { transcribeMediaUrl } from "./WhisperTranscriberEngine";

export const VideoTranscriptDialog = ({
  open,
  onClose,
  onSave,
  block,
  transcriber = transcribeMediaUrl,
}) => {
  const t = useScrollytellTranslation();
  const [localNoSpeech, setLocalNoSpeech] = useState(Boolean(block?.noSpeech));
  const [localTranscript, setLocalTranscript] = useState(block?.transcript || "");
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcribeProgress, setTranscribeProgress] = useState(null);
  const [transcribeError, setTranscribeError] = useState(null);
  const abortControllerRef = useRef(null);
  const videoRef = useRef(null);
  const [videoNode, setVideoNode] = useState(null);
  const hlsRef = useRef(null);

  const rawUrl = String(block?.videoUrl || block?.url || "").trim();
  let playerVideoUrl = "";
  let playerPoster = "";

  if (rawUrl) {
    if (
      rawUrl.startsWith("http://") ||
      rawUrl.startsWith("https://") ||
      rawUrl.startsWith("/") ||
      rawUrl.startsWith("blob:")
    ) {
      playerVideoUrl = rawUrl;
      const match = rawUrl.match(/^(https?:\/\/[^/]+)\/streaming\/adaptive\/([^/?#]+)/);
      if (match) {
        playerPoster = `${match[1]}/image/${match[2]}?dimension=1024x1024`;
      }
    } else {
      playerVideoUrl = `https://ems.dimu.org/streaming/adaptive/${rawUrl}/index.m3u8`;
      playerPoster = `https://ems.dimu.org/image/${rawUrl}?dimension=1024x1024`;
    }
  }

  useEffect(() => {
    if (open) {
      setLocalNoSpeech(Boolean(block?.noSpeech));
      setLocalTranscript(block?.transcript || "");
      setTranscribeError(null);
      setTranscribeProgress(null);
    } else {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
        setIsTranscribing(false);
      }
      if (videoNode && typeof videoNode.pause === "function") {
        videoNode.pause();
      }
    }
  }, [open, block?.noSpeech, block?.transcript, videoNode]);

  useEffect(() => {
    const currentVideo = videoRef.current || videoNode;
    if (!open || !playerVideoUrl || !currentVideo) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      return;
    }

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const isHls = playerVideoUrl.includes(".m3u8");
    if (isHls && Hls?.isSupported?.()) {
      const hls = new Hls({
        debug: false,
        progressive: true,
        forceKeyFrameOnDiscontinuity: false,
        testBandwidth: false,
        autoStartLoad: true,
      });
      hlsRef.current = hls;
      hls.loadSource(playerVideoUrl);
      hls.attachMedia(currentVideo);
    } else if (
      currentVideo.canPlayType &&
      currentVideo.canPlayType("application/vnd.apple.mpegurl") &&
      isHls
    ) {
      currentVideo.src = playerVideoUrl;
    } else {
      currentVideo.src = playerVideoUrl;
    }

    return () => {
      if (currentVideo && typeof currentVideo.pause === "function") {
        currentVideo.pause();
      }
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [open, playerVideoUrl, videoNode]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
    };
  }, []);

  const handleAutoTranscribe = async () => {
    if (!playerVideoUrl || localNoSpeech || isTranscribing) return;

    const initialTranscript = localTranscript;
    const hadInitialText = Boolean(
      initialTranscript && initialTranscript.trim().length > 0
    );

    setTranscribeError(null);
    setIsTranscribing(true);
    setTranscribeProgress({
      status: t("VideoBlockTranscribingStart", "Forbereder transkripsjon..."),
      progress: 0,
    });

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const text = await transcriber(playerVideoUrl, {
        signal: controller.signal,
        onProgress: (p) => {
          setTranscribeProgress({
            status: p.status || "",
            progress: typeof p.progress === "number" ? p.progress : 0,
          });
          if (!hadInitialText && p?.partialText) {
            setLocalTranscript(p.partialText);
          }
        },
      });

      if (text) {
        if (hadInitialText) {
          const replace =
            typeof window !== "undefined" && window.confirm
              ? window.confirm(
                  t(
                    "VideoBlockTranscriptReplaceConfirm",
                    "Feltet inneholder allerede tekst. Trykk OK for å erstatte, eller Avbryt for å tilføye på slutten."
                  )
                )
              : true;
          if (replace) {
            setLocalTranscript(text);
          } else {
            setLocalTranscript(`${initialTranscript.trim()}\n\n${text}`);
          }
        } else {
          setLocalTranscript(text);
        }
      }
    } catch (err) {
      if (err.name === "AbortError" || err.message?.includes("avbrutt")) {
        // Cancelled by user
      } else {
        setTranscribeError(
          err.message ||
            t(
              "VideoBlockTranscribeError",
              "Kunne ikke hente lyd fra videoen. Kontroller at videofilen er tilgjengelig og tillater avspilling."
            )
        );
      }
    } finally {
      setIsTranscribing(false);
      setTranscribeProgress(null);
      abortControllerRef.current = null;
    }
  };

  const handleCancelTranscribe = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsTranscribing(false);
    setTranscribeProgress(null);
  };

  const handleSave = () => {
    onSave({
      noSpeech: localNoSpeech,
      transcript: localTranscript,
    });
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
        {t("VideoBlockTranscriptDialogTitle", "Transkripsjon og tilgjengelighet for video")}
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1.15fr",
          "@media (max-width: 767px)": {
            gridTemplateColumns: "1fr",
          },
          gap: 2.5,
          alignItems: "start",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {playerVideoUrl && (
            <Box
              sx={{
                width: "100%",
                aspectRatio: "16 / 9",
                backgroundColor: "#000000",
                borderRadius: 1,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #334155",
              }}
            >
              <video
                ref={(node) => {
                  videoRef.current = node;
                  setVideoNode(node);
                }}
                controls
                playsInline
                preload="metadata"
                poster={playerPoster}
                data-testid="video-transcript-preview"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </Box>
          )}

          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              backgroundColor: "#f5f5f5",
              border: "1px solid #e0e0e0",
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(localNoSpeech)}
                  onChange={(e) => setLocalNoSpeech(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography sx={{ fontWeight: 500, fontSize: "0.95rem" }}>
                  {t(
                    "VideoBlockNoSpeechSwitchLabel",
                    "Videoen inneholder ikke tale (kun visuell / stemningsvideo)"
                  )}
                </Typography>
              }
            />
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5, pl: 6 }}>
              {t(
                "VideoBlockNoSpeechHelp",
                "Slå på denne hvis videoen er uten dialog eller fortellerstemme. Da kreves ikke transkripsjon etter WCAG 1.2.2."
              )}
            </Typography>
          </Box>
        </Box>

        {localNoSpeech ? (
          <Box
            sx={{
              p: 3,
              borderRadius: 1,
              backgroundColor: "#F8FAFC",
              border: "1px dashed #CBD5E1",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Typography sx={{ fontWeight: 600, color: "text.primary" }}>
              {t("VideoBlockNoSpeechConfirmedTitle", "Ingen transkripsjon nødvendig")}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t(
                "VideoBlockNoSpeechConfirmedDesc",
                "Videoen er markert som ren stemningsvideo uten tale. WCAG 1.2.2 krever ikke transkripsjon for videoer uten dialog."
              )}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<Sparkles size={16} />}
                onClick={handleAutoTranscribe}
                disabled={!playerVideoUrl || isTranscribing}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: "#0284C7",
                  color: "#0284C7",
                  "&:hover": {
                    borderColor: "#0369A1",
                    backgroundColor: "rgba(2, 132, 199, 0.08)",
                  },
                }}
              >
                {t(
                  "VideoBlockAutoTranscribeBtn",
                  "Generer transkripsjon automatisk (AI)"
                )}
              </Button>

              {isTranscribing && (
                <Button
                  variant="text"
                  size="small"
                  color="inherit"
                  onClick={handleCancelTranscribe}
                  aria-label="Avbryt transkripsjon"
                  sx={{
                    textTransform: "none",
                    fontSize: "12px",
                    color: "#666666",
                  }}
                >
                  {t("VideoBlockAutoTranscribeCancel", "Avbryt")}
                </Button>
              )}
            </Box>

            {isTranscribing && (
              <Box
                role="status"
                aria-live="polite"
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: "#F0F9FF",
                  border: "1px solid #BAE6FD",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.75,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "#0369A1", fontWeight: 600 }}
                >
                  {transcribeProgress?.status ||
                    t(
                      "VideoBlockTranscribingWait",
                      "Transkriberer tale med NB-Whisper AI..."
                    )}
                </Typography>
                <LinearProgress
                  variant={
                    transcribeProgress?.progress > 0
                      ? "determinate"
                      : "indeterminate"
                  }
                  value={transcribeProgress?.progress || 0}
                  aria-valuenow={transcribeProgress?.progress || 0}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: "#E0F2FE",
                    "& .MuiLinearProgress-bar": { backgroundColor: "#0284C7" },
                  }}
                />
              </Box>
            )}

            {transcribeError && (
              <Alert severity="error" onClose={() => setTranscribeError(null)}>
                {transcribeError}
              </Alert>
            )}

            <TextField
              fullWidth
              multiline
              minRows={8}
              inputProps={{ "data-a11y-field": "videoTranscript" }}
              label={t("VideoBlockTranscriptLabel", "Transkripsjon av video (WCAG 1.2.2)")}
              placeholder={t(
                "VideoBlockTranscriptPlaceholder",
                "Skriv eller lim inn det som sies i videoen..."
              )}
              value={localTranscript}
              onChange={(e) => setLocalTranscript(e.target.value)}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          {t("VideoBlockTranscriptCancel", "Avbryt")}
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {t("VideoBlockTranscriptSave", "Bruk")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
